import React, {
  useState,
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react';
import {
  ExternalLink,
  FileText,
  Eye,
  ChevronDown,
  ChevronUp,
  ToggleLeft,
  ToggleRight,
  Quote,
  BookOpen,
} from 'lucide-react';

const SourceDisplay = forwardRef(
  ({ sources, isDarkMode, colors, highlightedSourceIndex }, ref) => {
    const [showSources, setShowSources] = useState(false); // Default to hidden
    const [expandedExcerpts, setExpandedExcerpts] = useState(new Set());
    const sourceRefs = useRef([]);

    // Initialize refs array
    useEffect(() => {
      if (sources && Array.isArray(sources) && sources.length > 0) {
        sourceRefs.current = sourceRefs.current.slice(0, sources.length);
      } else {
        sourceRefs.current = [];
      }
    }, [sources]);

    // Expose scrollToSource method to parent components
    useImperativeHandle(ref, () => ({
      scrollToSource: sourceIndex => {
        // Show sources if hidden
        if (!showSources) {
          setShowSources(true);
          // Need a longer delay when opening the sources list for the first time
          // to ensure React has time to render the DOM elements
          setTimeout(() => {
            if (sourceRefs.current[sourceIndex]) {
              sourceRefs.current[sourceIndex].scrollIntoView({
                behavior: 'smooth',
                block: 'center',
              });
            }
          }, 300);
        } else {
          // If sources are already visible, scroll immediately
          if (sourceRefs.current[sourceIndex]) {
            sourceRefs.current[sourceIndex].scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        }
      },
    }), [showSources]); // Add showSources as a dependency

    if (!sources || sources.length === 0) {
      return null;
    }

    const toggleSourcesVisibility = () => {
      setShowSources(!showSources);
    };

    const toggleExcerptExpansion = (sourceIndex, excerptIndex) => {
      const key = `${sourceIndex}-${excerptIndex}`;
      const newExpanded = new Set(expandedExcerpts);
      if (newExpanded.has(key)) {
        newExpanded.delete(key);
      } else {
        newExpanded.add(key);
      }
      setExpandedExcerpts(newExpanded);
    };

    const handleSourceClick = (source, event) => {
      event.preventDefault();

      // Priority order: excerpt_url, presigned_url, then direct HTTPS URLs
      let urlToOpen = null;

      if (source.excerpt_url) {
        urlToOpen = source.excerpt_url;
      } else if (source.presigned_url) {
        urlToOpen = source.presigned_url;
      } else if (source.uri && source.uri.startsWith('https://')) {
        // For direct HTTPS URLs, add page fragment if available
        urlToOpen = source.uri;
        if (source.page_number) {
          urlToOpen += `#page=${source.page_number}`;
        }
      }

      if (urlToOpen) {
        try {
          window.open(urlToOpen, '_blank', 'noopener,noreferrer');
        } catch {
          // ESLint: removed console.error for production compliance
          // Fallback: try to navigate to the URL
          window.location.href = urlToOpen;
        }
      } else {
        // ESLint: removed console.warn for production compliance
      }
    };

    const isSourceClickable = source => {
      return (
        source.excerpt_url ||
        source.presigned_url ||
        (source.uri && source.uri.startsWith('https://'))
      );
    };

    const truncateText = (text, maxLength = 200) => {
      if (!text || text.length <= maxLength) {
        return text;
      }
      return text.substring(0, maxLength).trim() + '...';
    };

    const parseExcerpts = snippet => {
      if (!snippet) {
        return [];
      }

      // Split by double newlines and filter out empty strings
      const parts = snippet.split('\n\n').filter(part => part.trim());

      return parts.map((part, index) => {
        const match = part.match(/^\[([^\]]+)\]\s*(.*)/s);
        if (match) {
          return {
            id: index,
            header: match[1],
            content: match[2].trim(),
            isLabeled: true,
          };
        }
        return {
          id: index,
          content: part.trim(),
          isLabeled: false,
        };
      });
    };

    return (
      <div className='mt-4 space-y-3'>
        {/* Toggle Button */}
        <div className='flex items-center justify-between'>
          <div className='flex items-center space-x-2'>
            <FileText
              className='w-5 h-5'
              style={{
                color: isDarkMode ? colors.darkBlue : colors.primaryBlue,
              }}
            />
            <span
              className='text-base font-semibold'
              style={{
                color: isDarkMode ? colors.darkText : colors.textPrimary,
              }}
            >
              Sources ({sources.length})
            </span>
          </div>
          <button
            onClick={toggleSourcesVisibility}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md text-base font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              isDarkMode
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white focus:ring-blue-500'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700 hover:text-gray-900 focus:ring-blue-500'
            }`}
            title={showSources ? 'Hide sources' : 'Show sources'}
          >
            {showSources ? (
              <Eye
                className='w-5 h-5'
                style={{
                  color: isDarkMode ? colors.darkBlue : colors.primaryBlue,
                }}
              />
            ) : (
              <Eye
                className='w-5 h-5 opacity-50'
                style={{
                  color: isDarkMode ? colors.darkText : colors.textSecondary,
                }}
              />
            )}
            <span>{showSources ? 'Hide Sources' : 'Show Sources'}</span>
          </button>
        </div>

        {/* Sources List - Only show when toggle is on */}
        {showSources && (
          <div className='space-y-3'>
            {sources.map((source, index) => {
              const hasSnippet =
                source.snippet && source.snippet.trim().length > 0;
              const isMultiPageSource =
                source.merged_from_count > 1 ||
                (source.page_numbers && source.page_numbers.length > 1);
              const excerpts = parseExcerpts(source.snippet);
              const hasMultipleExcerpts = excerpts.length > 1;

              return (
                <div
                  key={index}
                  ref={el => (sourceRefs.current[index] = el)}
                  className={`border rounded-lg overflow-hidden transition-all duration-300 shadow-sm hover:shadow-md ${
                    isDarkMode
                      ? 'border-gray-600 bg-gray-800 hover:bg-gray-750'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                  } ${highlightedSourceIndex === index ? 'ring-2 ring-blue-500 ring-opacity-50' : ''}`}
                >
                  {/* Source Header */}
                  <div className='p-4'>
                    <div className='flex items-start justify-between'>
                      <div className='flex-1 min-w-0'>
                        <div className='flex items-center space-x-2 flex-wrap mb-2'>
                          {isSourceClickable(source) ? (
                            <button
                              onClick={event =>
                                handleSourceClick(source, event)
                              }
                              className={`flex items-center space-x-2 text-base font-semibold hover:underline focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded transition-colors duration-200 ${
                                isDarkMode
                                  ? 'text-blue-400 hover:text-blue-300'
                                  : 'text-blue-600 hover:text-blue-800'
                              }`}
                              title={`Open ${source.title || 'document'} in new tab`}
                            >
                              <ExternalLink className='w-4 h-4 flex-shrink-0' />
                              <span className='truncate'>
                                {source.title ||
                                  source.uri?.split('/').pop() ||
                                  'Source Document'}
                              </span>
                            </button>
                          ) : (
                            <div className='flex items-center space-x-2'>
                              <FileText
                                className='w-4 h-4 flex-shrink-0'
                                style={{
                                  color: isDarkMode
                                    ? colors.darkText
                                    : colors.textSecondary,
                                }}
                              />
                              <span
                                className={`text-base font-semibold truncate ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
                              >
                                {source.title ||
                                  source.uri?.split('/').pop() ||
                                  'Source Document'}
                              </span>
                            </div>
                          )}

                          {/* Excerpt URL indicator */}
                          {source.excerpt_url && (
                            <span
                              className={`text-xs px-2 py-1 rounded flex items-center space-x-1 ${
                                isDarkMode
                                  ? 'bg-green-900 text-green-300'
                                  : 'bg-green-100 text-green-800'
                              }`}
                            >
                              <BookOpen className='w-3 h-3' />
                              <span>Direct excerpt link</span>
                            </span>
                          )}

                          {/* Page number badge for single page sources */}
                          {source.page_number && !isMultiPageSource && (
                            <span
                              className={`text-xs px-2 py-1 rounded ${
                                isDarkMode
                                  ? 'bg-gray-700 text-gray-300'
                                  : 'bg-gray-200 text-gray-600'
                              }`}
                            >
                              Page {source.page_number}
                            </span>
                          )}

                          {/* Multiple excerpts badge */}
                          {isMultiPageSource && (
                            <span
                              className={`text-xs px-2 py-1 rounded ${
                                isDarkMode
                                  ? 'bg-blue-900 text-blue-300'
                                  : 'bg-blue-100 text-blue-800'
                              }`}
                            >
                              {source.merged_from_count
                                ? `${source.merged_from_count} excerpts`
                                : 'Multiple pages'}
                            </span>
                          )}
                        </div>

                        {/* URI display for reference */}
                        {source.uri && (
                          <div
                            className={`text-xs mb-2 truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
                          >
                            {source.uri}
                          </div>
                        )}

                        {/* Page numbers summary for multi-page sources */}
                        {source.page_numbers &&
                          source.page_numbers.length > 1 && (
                            <div
                              className={`text-xs mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
                            >
                              Pages: {source.page_numbers.join(', ')}
                            </div>
                          )}

                        {/* Excerpt ID display */}
                        {source.excerpt_id && (
                          <div
                            className={`text-xs mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
                          >
                            Excerpt ID: {source.excerpt_id}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Excerpt Preview - Always visible */}
                  {hasSnippet && (
                    <div
                      className={`border-t px-4 py-3 ${isDarkMode ? 'border-gray-600 bg-gray-750' : 'border-gray-200 bg-gray-50'}`}
                    >
                      <div className='flex items-start space-x-3'>
                        <Quote
                          className={`w-4 h-4 mt-1 flex-shrink-0 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}
                        />
                        <div className='flex-1'>
                          <div
                            className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}
                          >
                            Excerpt Preview:
                          </div>
                          {hasMultipleExcerpts ? (
                            <div className='space-y-2'>
                              {excerpts
                                .slice(0, 2)
                                .map((excerpt, excerptIndex) => {
                                  const isExcerptExpanded =
                                    expandedExcerpts.has(
                                      `${index}-${excerptIndex}`
                                    );
                                  const previewText = truncateText(
                                    excerpt.content,
                                    150
                                  );
                                  const needsExpansion =
                                    excerpt.content.length > 150;

                                  return (
                                    <div
                                      key={excerptIndex}
                                      className={`p-3 rounded-md border-l-4 ${
                                        isDarkMode
                                          ? 'bg-gray-700 border-blue-500'
                                          : 'bg-white border-blue-400'
                                      }`}
                                    >
                                      {excerpt.isLabeled && (
                                        <div
                                          className={`text-xs font-semibold mb-1 ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}
                                        >
                                          {excerpt.header}
                                        </div>
                                      )}
                                      <p
                                        className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}
                                      >
                                        {isExcerptExpanded
                                          ? excerpt.content
                                          : previewText}
                                      </p>
                                      {needsExpansion && (
                                        <button
                                          onClick={() =>
                                            toggleExcerptExpansion(
                                              index,
                                              excerptIndex
                                            )
                                          }
                                          className={`mt-2 text-xs font-medium hover:underline ${
                                            isDarkMode
                                              ? 'text-blue-400 hover:text-blue-300'
                                              : 'text-blue-600 hover:text-blue-800'
                                          }`}
                                        >
                                          {isExcerptExpanded
                                            ? 'Show less'
                                            : 'Read more'}
                                        </button>
                                      )}
                                    </div>
                                  );
                                })}
                              {excerpts.length > 2 && (
                                <div
                                  className={`text-xs text-center py-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
                                >
                                  {excerpts.length - 2} more excerpt
                                  {excerpts.length - 2 !== 1 ? 's' : ''}{' '}
                                  available below
                                </div>
                              )}
                            </div>
                          ) : (
                            <div
                              className={`p-3 rounded-md border-l-4 ${
                                isDarkMode
                                  ? 'bg-gray-700 border-blue-500'
                                  : 'bg-white border-blue-400'
                              }`}
                            >
                              {excerpts.length > 0 && (
                                <>
                                  {excerpts[0].isLabeled && (
                                    <div
                                      className={`text-xs font-semibold mb-1 ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}
                                    >
                                      {excerpts[0].header}
                                    </div>
                                  )}
                                  <p
                                    className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}
                                  >
                                    {expandedExcerpts.has(`${index}-0`)
                                      ? excerpts[0].content
                                      : truncateText(excerpts[0].content, 150)}
                                  </p>
                                  {excerpts[0].content.length > 150 && (
                                    <button
                                      onClick={() =>
                                        toggleExcerptExpansion(index, 0)
                                      }
                                      className={`mt-2 text-xs font-medium hover:underline ${
                                        isDarkMode
                                          ? 'text-blue-400 hover:text-blue-300'
                                          : 'text-blue-600 hover:text-blue-800'
                                      }`}
                                    >
                                      {expandedExcerpts.has(`${index}-0`)
                                        ? 'Show less'
                                        : 'Read more'}
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }
);

SourceDisplay.displayName = 'SourceDisplay';

export default SourceDisplay;
